/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { Box, IconButton, Text } from '@chakra-ui/react';
import { useField, useFormState } from 'react-final-form';
import {
  QuickSearch,
  Plate,
  InfoMaster,
  DetailsMaster,
  ModalPage,
  NotFoundAlert,
  InfoIcon,
} from 'components';
import { MasterInfo, ServiceGroupsInfo } from 'types';
import { AngleRightIcon } from 'static/icons';

const AngleRightIconStyled = styled(AngleRightIcon)`
  color: ${({ theme }) => theme.colors.asphalt[2]};
`;

export const Master: React.FC<{
  name: string;
  masters: MasterInfo[];
  possibleMastersIds?: string[];
  possibleMastersByDayIds?: string[];
  serviceGroups: ServiceGroupsInfo;
  widgetId?: string;
  isPossibleMastersExist: boolean;
  setPossibleMastersIds: (ids?: string[]) => void;
  specialistCases: {
    specialistNominativeCase: string;
    specialistGenitiveCase: string;
    specialistPrepositionalCase: string;
  };
}> = ({
  name,
  masters,
  possibleMastersIds,
  possibleMastersByDayIds,
  widgetId,
  isPossibleMastersExist,
  setPossibleMastersIds,
  specialistCases,
}) => {
  const {
    specialistNominativeCase,
    specialistGenitiveCase,
    specialistPrepositionalCase,
  } = specialistCases;
  const {
    values: { favorIds },
  } = useFormState<{ favorIds?: string[] }>();

  const { input } = useField(name);

  const possibleMastersByDay =
    !favorIds && possibleMastersByDayIds
      ? masters.filter((master) => possibleMastersByDayIds.includes(master.id))
      : masters;

  const possibleMasters =
    favorIds && possibleMastersIds
      ? masters.filter((master) => possibleMastersIds.includes(master.id))
      : possibleMastersByDay;

  useEffect(() => {
    if (!favorIds) {
      setPossibleMastersIds();
    }
  }, [favorIds]);

  // Быстрый поиск
  const [filter, setFilter] = React.useState('');
  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setFilter(event.target.value);
  const filteredMasters = React.useMemo(() => {
    const newFilter = filter?.toLowerCase();
    return filter
      ? possibleMasters.filter(
          (master) =>
            master.name?.toLowerCase().includes(newFilter) ||
            master.position?.toLowerCase().includes(newFilter),
        )
      : possibleMasters;
  }, [filter, possibleMasters]);

  const setInputValue = (value: string) => () => {
    input.onChange(value);
  };

  const [itemId, setItemId] = React.useState<{
    id: string;
    tabIdx: number;
  } | null>(null);
  const resetItemId = () => setItemId(null);
  const item = itemId && masters.find(({ id }) => id === itemId.id);

  const anyMastersLength = filteredMasters.filter(
    (master) => master.anyEmployee,
  ).length;

  const visibleMasters = filteredMasters.filter(
    (master) => master.isShow !== false,
  );

  const isShowAnyMaster = isPossibleMastersExist && anyMastersLength > 0;

  const handleOpenDetailsMaster =
    (masterId: string, tabIdx = 0) =>
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      setItemId({ id: masterId, tabIdx });
      e.stopPropagation();
    };

  return (
    <>
      <ModalPage
        isOpen={!!item}
        title={specialistPrepositionalCase}
        onClose={resetItemId}
      >
        {item && (
          <DetailsMaster
            {...item}
            widgetId={widgetId}
            tabIndex={itemId?.tabIdx || 0}
          />
        )}
      </ModalPage>
      <Box>
        <QuickSearch
          placeholder={`Поиск ${specialistGenitiveCase}`}
          value={filter}
          onChange={handleFilterChange}
        />
        {isShowAnyMaster && (
          <Plate borderRadius="lg" mb={4} onClick={setInputValue('any')}>
            <Text color="black" textStyle="h4" ml={4}>
              {`Любой ${specialistNominativeCase}`}
            </Text>
            <AngleRightIconStyled />
          </Plate>
        )}
        {visibleMasters.length ? (
          <Box w="100%" borderRadius="lg" borderColor="grayBorder">
            {visibleMasters.map((master) => {
              return (
                <Plate key={master.id} onClick={setInputValue(master.id)}>
                  <InfoMaster {...master} widgetId={widgetId} />
                  <IconButton
                    rounded="full"
                    variant="ghost"
                    icon={<InfoIcon />}
                    aria-label="Назад в выбор услуг"
                    onClick={handleOpenDetailsMaster(master.id)}
                  />
                </Plate>
              );
            })}
          </Box>
        ) : (
          <NotFoundAlert
            text={`${
              specialistNominativeCase[0].toUpperCase() +
              specialistNominativeCase.slice(1)
            } не найден`}
          />
        )}
      </Box>
    </>
  );
};
